import { useEffect, useMemo, useRef, useState } from "react";
import {
	Button,
	Card,
	Collapse,
	ColorPicker,
	DatePicker,
	Flex,
	Form,
	Input,
	InputNumber,
	Modal,
	Popconfirm,
	Select,
	Table,
	Tag,
	Tooltip,
	Typography,
} from "antd";
import {
	DeleteFilled,
	EditFilled,
	EyeFilled,
	GiftOutlined,
	InfoCircleOutlined,
	LoadingOutlined,
	PlayCircleFilled,
} from "@ant-design/icons";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useDispatch, useSelector } from "react-redux";
import {
	deletePromotion,
	fetchPromotions,
	savePromotion,
} from "../../redux/promotionsSlice";
import "../../pages/CustomUrl/CustomUrl.css";
import "./Promotions.css";

dayjs.extend(utc);
dayjs.extend(timezone);

const { Title } = Typography;
const { Panel } = Collapse;

const money = (cents) => `$${((Number(cents) || 0) / 100).toFixed(2)}`;

const customerTypeOptions = [
	{ value: "new", label: "New" },
	{ value: "existing", label: "Existing" },
	{ value: "all", label: "All" },
];

const audienceOptions = [
	{ value: "all", label: "Both" },
	{ value: "member", label: "Members" },
	{ value: "non_member", label: "Non-members" },
];

const customerTypeLabel = (value) =>
	customerTypeOptions.find((option) => option.value === (value || "all"))?.label || "All";

const audienceLabel = (value) =>
	audienceOptions.find((option) => option.value === (value || "all"))?.label || "Both";

const statusColor = {
	active: "green",
	inactive: "default",
};

const displayStatus = (status) => (status === "active" || status === "published" ? "active" : "inactive");

const FieldTip = ({ text }) => (
	<Tooltip title={text}>
		<InfoCircleOutlined style={{ fontSize: "14px", color: "#1890ff", cursor: "help" }} />
	</Tooltip>
);

const jobKey = (name = "") => name.trim().replace(/\s+/g, " ").toLowerCase();

const orgStamp = (value, timeZone) =>
	value ? dayjs(value).tz(timeZone || "UTC").format("YYYY-MM-DD HH:mm") : "";

const rowsFor = (parent, key) => {
	if (!parent) return [];
	if (Array.isArray(parent[key])) return parent[key];
	const extra = parent.additionalServices;
	const value = extra && typeof extra === "object" ? extra[key] : null;
	return Array.isArray(value) ? value : [];
};

const serviceKeys = (parent) => {
	const extra = parent?.additionalServices;
	const extraKeys = extra && typeof extra === "object" && !Array.isArray(extra) ? Object.keys(extra) : [];
	return ["repair", "maintenance", "estimate", ...extraKeys];
};

const mappedJobName = (row, jobs) => {
	const info = (jobs || []).find((job) => String(job._id) === String(row?.jobItem?._id || row?.jobItem));
	return info?.jobItem || row?.st_non_member_job_name || row?.st_member_job_name || "";
};

const folderIsJob = (folder) =>
	Boolean(folder?.is_job) ||
	(Array.isArray(folder?.jobs) && folder.jobs.length === 1 && folder.jobs[0] === folder.category_name);

const capitalize = (value = "") => value.charAt(0).toUpperCase() + value.slice(1);

const mutedText = { color: "#6b7280" };
const emptyEquipment = [];

const fieldStyle = { borderRadius: "8px" };
const actionIcon = { fontSize: 20, cursor: "pointer" };
const BannerColorPicker = ({ value, onChange }) => (
	<ColorPicker
		format="hex"
		disabledAlpha
		value={value || null}
		onChange={(color) => onChange?.(color?.toHexString?.() || null)}
	>
		<button
			type="button"
			style={{
				display: "inline-flex",
				alignItems: "center",
				gap: 8,
				padding: "4px 10px",
				borderRadius: 8,
				border: "1px solid #e5e7eb",
				background: "#fff",
				cursor: "pointer",
				color: "#374151",
				fontSize: 13,
			}}
		>
			<span
				style={{
					width: 16,
					height: 16,
					borderRadius: 4,
					background: value || "transparent",
					border: value ? "none" : "1px dashed #9ca3af",
				}}
			/>
			<span>{value || "Organization color"}</span>
		</button>
	</ColorPicker>
);

const SchedulerPreview = ({ title, color, regularCents, promoCents, validThrough }) => {
	const regular = (Number(regularCents) || 0) / 100;
	const promo = (Number(promoCents) || 0) / 100;
	const savings = regular > promo ? regular - promo : 0;
	return (
		<div
			className="promo-mockup promo_banner"
			style={{ "--promo-color": color, "--promo-tint": `${color}33`, "--promo-wash": `${color}14` }}
		>
			<div className="promo_banner_top">
				<span className="promo_banner_label">
					<svg viewBox="0 0 24 24" aria-hidden="true">
						<path d="M20 12v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-8h16zm0-2H4V6a2 2 0 0 1 2-2h4.2a3 3 0 0 1 5.6 0H18a2 2 0 0 1 2 2v4zM12 4a1 1 0 0 0-1 1h2a1 1 0 0 0-1-1zm-1 8h2v10h-2V12z" />
					</svg>
					Special offer
				</span>
				<span className="promo_banner_save">Save ${savings.toFixed(2)}</span>
			</div>
			<div className="promo_banner_body">
				<div className="promo_banner_title">{title || "Promotion name"}</div>
				<div className="promo_banner_prices">
					<span className="promo_banner_regular">{`$${regular.toFixed(2)}`}</span>
					<span className="promo_banner_price">{`$${promo.toFixed(2)}`}</span>
				</div>
				{validThrough ? <div className="promo_banner_until">Valid through {validThrough}</div> : null}
			</div>
		</div>
	);
};

const primaryButtonStyle = {
	background: "rgb(4 42 87)",
	color: "#fff",
	fontSize: "12px",
	padding: "4px 12px",
	borderRadius: "5px",
	border: "none",
};

export default function Promotions() {
	const dispatch = useDispatch();
	const [form] = Form.useForm();
	const organizationId = useSelector((state) => state.auth.currentOrganizationId);
	const organizationData = useSelector((state) => state.organization.organizationData);
	const provider = organizationData?.organization_provider;
	const primaryColor = organizationData?.primaryColor || "#DA1A32";
	const { list, loading } = useSelector((state) => state.promotions);
	const { trades = [] } = useSelector((state) => state.trade);
	const { jobs: backendJobs = [] } = useSelector((state) => state.jobs);
	const [open, setOpen] = useState(false);
	const [editingId, setEditingId] = useState(null);
	const [saving, setSaving] = useState(false);
	const [pageSize, setPageSize] = useState(5);
	const [viewing, setViewing] = useState(null);
	const [rowAction, setRowAction] = useState(null);
	const rowActionRef = useRef(null);
	const tradeId = Form.useWatch("tradeId", form);
	const serviceType = Form.useWatch("serviceType", form);
	const selectedFolder = Form.useWatch("folder", form);
	const regularPrice = Form.useWatch("regularPrice", form);
	const promoPrice = Form.useWatch("promoPrice", form);
	const scheduleMode = Form.useWatch("scheduleMode", form) || "scheduled";
	const customerType = Form.useWatch("customerType", form) || "all";
	const timeZone = organizationData?.organization_timeZone || "UTC";

	useEffect(() => {
		if (!organizationId) return;
		dispatch(fetchPromotions(organizationId));
	}, [dispatch, organizationId]);

	const tradeOptions = useMemo(() => {
		const options = [];
		for (const navigation of organizationData?.navigation_mapping || []) {
			if (navigation?.isActive === false || !navigation?.trade?.name) continue;
			const offered = trades.find((trade) => trade.tradeName === navigation.trade.name);
			const id = String(offered?._id || "");
			if (!id || options.some((option) => option.id === id)) continue;
			options.push({ id, name: navigation.trade.name });
		}
		return options.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
	}, [organizationData, trades]);

	const serviceTypes = useMemo(() => {
		const trade = tradeOptions.find((option) => option.id === tradeId);
		const navigation = (organizationData?.navigation_mapping || []).find((entry) => entry?.trade?.name === trade?.name);
		const jobs = (organizationData?.jobs_mapping || []).find(
			(row) => String(row?.trade?._id || row?.trade) === String(tradeId)
		);
		if (!navigation || !jobs) return [];
		return serviceKeys(navigation).filter(
			(key) =>
				rowsFor(navigation, key).some((folder) => folder?.is_active !== false && folder?.category_name) &&
				rowsFor(jobs, key).some((row) => row?.is_active !== false)
		);
	}, [organizationData, tradeOptions, tradeId]);

	const folders = useMemo(() => {
		const trade = tradeOptions.find((option) => option.id === tradeId);
		const navigation = (organizationData?.navigation_mapping || []).find((entry) => entry?.trade?.name === trade?.name);
		const jobs = (organizationData?.jobs_mapping || []).find(
			(row) => String(row?.trade?._id || row?.trade) === String(tradeId)
		);
		if (!navigation || !jobs || !serviceType) return [];
		const mappings = rowsFor(jobs, serviceType).filter((row) => row?.is_active !== false);
		const groups = [];
		for (const folder of rowsFor(navigation, serviceType)) {
			if (folder?.is_active === false || !folder?.category_name) continue;
			const isJob = folderIsJob(folder);
			const requested = isJob ? [folder.category_name, ...(folder.jobs || [])] : folder.jobs || [];
			const equipment = new Map();
			for (const name of requested) {
				if (!name || /^[a-f0-9]{24}$/i.test(name)) continue;
				const matches = mappings.filter((row) => jobKey(mappedJobName(row, backendJobs)) === jobKey(name));
				if (!matches.length) continue;
				const key = jobKey(name);
				equipment.set(key, {
					key,
					name,
					ids: matches.map((row) => ({
						mappingItemId: String(row._id),
						jobItemId: String(row?.jobItem?._id || row?.jobItem || ""),
						categoryName: folder.category_name,
						name,
						isJob,
					})),
				});
			}
			if (!equipment.size) continue;
			groups.push({
				name: folder.category_name,
				isJob,
				equipment: [...equipment.values()].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" })),
			});
		}
		return groups.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
	}, [organizationData, backendJobs, tradeOptions, tradeId, serviceType]);

	const currentFolder = folders.find((folder) => folder.name === selectedFolder);
	const equipmentOptions = currentFolder?.equipment ?? emptyEquipment;

	const closeModal = () => {
		setOpen(false);
		setEditingId(null);
		form.resetFields();
	};

	const openCreate = () => {
		setEditingId(null);
		form.resetFields();
		form.setFieldsValue({ status: "inactive", customerType: "all", audience: "all", scheduleMode: "scheduled", regularPrice: 0, promoPrice: 0 });
		setOpen(true);
	};

	const openEdit = (record) => {
		const equipmentKeys = [];
		const folderName = record.items?.find((item) => item.categoryName)?.categoryName || "";
		for (const item of record.items || []) {
			const key = jobKey(item.name || "");
			if (key && !equipmentKeys.includes(key)) equipmentKeys.push(key);
		}
		setEditingId(record._id);
		form.setFieldsValue({
			title: record.title,
			tradeId: record.tradeId,
			serviceType: record.serviceType,
			customerType: record.customerType || "all",
			audience: record.customerType === "new" ? "non_member" : record.audience || "all",
			bannerColor: record.bannerColor || null,
			folder: folderName || undefined,
			equipmentKeys,
			regularPrice: (record.regularPriceCents || 0) / 100,
			promoPrice: (record.promoPriceCents || 0) / 100,
			scheduleMode: record.scheduleMode || (record.endsAt ? "scheduled" : "until_disabled"),
			startsAt: record.startsAt ? dayjs(record.startsAt).tz(timeZone) : null,
			endsAt: record.endsAt ? dayjs(record.endsAt).tz(timeZone) : null,
			status: displayStatus(record.status),
		});
		setOpen(true);
	};

	const onSubmit = async () => {
		const values = await form.validateFields();
		const folder = folders.find((entry) => entry.name === values.folder);
		const items = (values.equipmentKeys || []).flatMap((key) => {
			const equipment = folder?.equipment?.find((entry) => entry.key === key);
			return (equipment?.ids || []).map((row) => ({
				mappingItemId: row.mappingItemId,
				jobItemId: row.jobItemId || "",
				categoryName: row.categoryName,
				name: row.name,
				isJob: Boolean(row.isJob),
			}));
		});
		setSaving(true);
		const saved = await dispatch(
			savePromotion({
				organizationId,
				promotionId: editingId,
				payload: {
					title: values.title,
					tradeId: values.tradeId,
					tradeName: tradeOptions.find((trade) => trade.id === values.tradeId)?.name || "",
					serviceType: values.serviceType,
					customerType: values.customerType || "all",
					audience: values.customerType === "new" ? "non_member" : values.audience || "all",
					bannerColor: values.bannerColor || "",
					items,
					regularPrice: values.regularPrice,
					promoPrice: values.promoPrice,
					scheduleMode: values.scheduleMode || "scheduled",
					startsAt: values.startsAt?.format("YYYY-MM-DDTHH:mm") || null,
					endsAt: values.scheduleMode === "until_disabled" ? null : values.endsAt?.format("YYYY-MM-DDTHH:mm"),
					status: values.status || "inactive",
				},
			})
		);
		setSaving(false);
		if (saved.meta.requestStatus === "fulfilled") {
			closeModal();
			dispatch(fetchPromotions(organizationId));
		}
	};

	const runRowAction = async (record, kind, task) => {
		if (rowActionRef.current) return;
		rowActionRef.current = { id: record._id, kind };
		setRowAction(rowActionRef.current);
		try {
			await task();
		} finally {
			rowActionRef.current = null;
			setRowAction(null);
		}
	};

	const changeStatus = async (record, status) => {
		const saved = await dispatch(
			savePromotion({
				organizationId,
				promotionId: record._id,
				payload: {
					title: record.title,
					tradeId: record.tradeId,
					tradeName: record.tradeName,
					serviceType: record.serviceType,
					customerType: record.customerType || "all",
					audience: record.customerType === "new" ? "non_member" : record.audience || "all",
					bannerColor: record.bannerColor || "",
					items: record.items,
					regularPrice: (record.regularPriceCents || 0) / 100,
					promoPrice: (record.promoPriceCents || 0) / 100,
					scheduleMode: record.scheduleMode || (record.endsAt ? "scheduled" : "until_disabled"),
					startsAt: record.startsAt,
					endsAt: record.scheduleMode === "until_disabled" ? null : record.endsAt,
					status,
				},
			})
		);
		if (saved.meta.requestStatus === "fulfilled") dispatch(fetchPromotions(organizationId));
	};

	const columns = [
		{
			title: "Promotion Name",
			dataIndex: "title",
			key: "title",
			render: (text) => <span style={{ fontWeight: "600", color: "#6b7280" }}>{text}</span>,
		},
		{
			title: "Trade",
			dataIndex: "tradeName",
			key: "tradeName",
			render: (text) => <span style={mutedText}>{text || "—"}</span>,
		},
		{
			title: "Service Type",
			dataIndex: "serviceType",
			key: "serviceType",
			render: (text) => <span style={mutedText}>{capitalize(text)}</span>,
		},
		{
			title: "Customer Type",
			dataIndex: "customerType",
			key: "customerType",
			render: (value) => <span style={mutedText}>{customerTypeLabel(value)}</span>,
		},
		{
			title: "Memberships",
			dataIndex: "audience",
			key: "audience",
			render: (value, record) => (
				<span style={mutedText}>{record.customerType === "new" ? "Non-members" : audienceLabel(value)}</span>
			),
		},
		{
			title: "Jobs",
			key: "jobs",
			render: (_, record) => {
				const seen = new Set();
				const lines = (record.items || []).filter((item) => {
					const key = `${item.categoryName || ""}|${item.name}`;
					if (seen.has(key)) return false;
					seen.add(key);
					return true;
				});
				const jobLabel = (item) => (item.categoryName ? `${item.categoryName} / ${item.name}` : item.name);
				const visible = lines.slice(0, 2);
				const hiddenCount = lines.length - visible.length;
				return (
					<div style={{ ...mutedText, fontSize: "13px" }}>
						{visible.map((item) => (
							<div key={`${item.categoryName}-${item.name}`}>{jobLabel(item)}</div>
						))}
						{hiddenCount > 0 && (
							<Tooltip
								title={
									<div>
										{lines.map((item) => (
											<div key={`${item.categoryName}-${item.name}`}>{jobLabel(item)}</div>
										))}
									</div>
								}
							>
								<span className="promo-jobs-more">+{hiddenCount} more</span>
							</Tooltip>
						)}
					</div>
				);
			},
		},
		{
			title: "Price",
			key: "price",
			render: (_, record) => (
				<span style={{ ...mutedText, fontSize: "13px", whiteSpace: "nowrap" }}>
					<s>{money(record.regularPriceCents)}</s> {money(record.promoPriceCents)}
				</span>
			),
		},
		{
			title: "Dates",
			key: "dates",
			render: (_, record) => (
				<span style={{ ...mutedText, fontSize: "13px" }}>
					{record.startsAt ? orgStamp(record.startsAt, timeZone) : "Starts when activated"}
					<br />
					{record.endsAt ? orgStamp(record.endsAt, timeZone) : "On until turned off"}
				</span>
			),
		},
		{
			title: "Status",
			key: "status",
			render: (_, record) => (
				<Tag color={statusColor[displayStatus(record.status)]}>{capitalize(displayStatus(record.status))}</Tag>
			),
		},
		{
			title: "Action",
			key: "action",
			width: 108,
			render: (_, record) => {
				const busy = Boolean(rowAction);
				const loadingKind = rowAction?.id === record._id ? rowAction.kind : "";
				const iconStyle = (color) => ({
					...actionIcon,
					color,
					pointerEvents: busy ? "none" : "auto",
					opacity: busy ? 0.45 : 1,
				});
				return (
					<div className="promo-row-actions">
						<Tooltip title="View">
							<EyeFilled style={iconStyle("#6b7280")} onClick={() => !busy && setViewing(record)} />
						</Tooltip>
						<Tooltip title="Edit">
							<EditFilled style={iconStyle("#1890ff")} onClick={() => !busy && openEdit(record)} />
						</Tooltip>
						{displayStatus(record.status) !== "active" && (
							<Tooltip title="Set active">
								{loadingKind === "active" ? (
									<LoadingOutlined style={{ ...actionIcon, color: "#52c41a" }} spin />
								) : (
									<PlayCircleFilled
										style={iconStyle("#52c41a")}
										onClick={() => runRowAction(record, "active", () => changeStatus(record, "active"))}
									/>
								)}
							</Tooltip>
						)}
						<Popconfirm
							title="Delete Promotion"
							description="Are you sure you want to delete this promotion?"
							onConfirm={() =>
								runRowAction(record, "delete", async () => {
									const removed = await dispatch(
										deletePromotion({ organizationId, promotionId: record._id })
									);
									if (removed.meta.requestStatus === "fulfilled") {
										dispatch(fetchPromotions(organizationId));
									}
								})
							}
							okText="Yes"
							cancelText="No"
							disabled={busy}
						>
							<Tooltip title="Delete">
								{loadingKind === "delete" ? (
									<LoadingOutlined style={{ ...actionIcon, color: "#ff4d4f" }} spin />
								) : (
									<DeleteFilled style={iconStyle("#ff4d4f")} />
								)}
							</Tooltip>
						</Popconfirm>
					</div>
				);
			},
		},
	];

	const header = (
		<Flex align="center" gap={10} style={{ marginBottom: "20px" }}>
			<GiftOutlined style={{ fontSize: 20 }} />
			<Title level={3} style={{ margin: 0 }}>
				Promotions
			</Title>
		</Flex>
	);

	if (provider === "HCP") {
		return (
			<>
				{header}
				<span style={mutedText}>Promotions are available for ServiceTitan organizations only.</span>
			</>
		);
	}

	return (
		<>
			{header}

			<div style={{ padding: "24px", maxWidth: "100%", margin: "0 auto" }}>
				<div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "16px" }}>
					<Button type="default" style={primaryButtonStyle} onClick={openCreate}>
						Create New Promotion
					</Button>
				</div>

				<Card
					bordered={false}
					style={{
						width: "100%",
						boxShadow: "0 4px 16px rgba(0, 0, 0, 0.1)",
						borderRadius: "10px",
					}}
				>
					<Table
						dataSource={list || []}
						columns={columns}
						rowKey="_id"
						loading={loading}
						pagination={{
							defaultPageSize: 5,
							pageSize,
							showSizeChanger: true,
							showQuickJumper: true,
							pageSizeOptions: ["5", "10", "20", "50"],
						}}
						onChange={(pagination) => setPageSize(pagination?.pageSize ?? 5)}
						style={{ marginTop: "16px" }}
					/>
				</Card>
			</div>

			<Modal
				className="custom-url-modal"
				title={editingId ? "Update Promotion" : "Create Promotion"}
				open={open}
				onCancel={closeModal}
				destroyOnClose
				width={720}
				footer={[
					<Button
						key="cancel"
						onClick={closeModal}
						style={{ fontSize: "12px", padding: "4px 12px", borderRadius: "5px", color: "#6b7280" }}
					>
						Cancel
					</Button>,
					<Button
						key="save"
						type="primary"
						loading={saving}
						onClick={onSubmit}
						style={{ ...primaryButtonStyle, backgroundColor: "rgb(4 42 87)" }}
					>
						{editingId ? "Update Promotion" : "Create Promotion"}
					</Button>,
				]}
			>
				<Form form={form} layout="vertical" requiredMark={false}>
					<Collapse defaultActiveKey={["1", "2", "3"]} className="custom-url-panel">
						<Panel header="Step 1: Promotion Details" key="1">
							<Form.Item
								name="title"
								label={<span className="custom-url-field-label">Promotion Name</span>}
								rules={[
									{ required: true, message: "Please enter a promotion name." },
									{ max: 40, message: "Promotion name can be at most 40 characters." },
								]}
							>
								<Input placeholder="e.g., Spring Tune Up" maxLength={40} showCount style={fieldStyle} />
							</Form.Item>
							<Flex gap={12} wrap="wrap">
								<Form.Item
									name="tradeId"
									label={<span className="custom-url-field-label">Trade</span>}
									rules={[{ required: true, message: "Please select a trade." }]}
									style={{ flex: 1, minWidth: 200 }}
								>
									<Select
										placeholder="Select Trade"
										options={tradeOptions.map((trade) => ({
											value: trade.id,
											label: trade.name,
										}))}
										onChange={() => form.setFieldsValue({ serviceType: undefined, folder: undefined, equipmentKeys: [] })}
									/>
								</Form.Item>
								<Form.Item
									name="serviceType"
									label={<span className="custom-url-field-label">Service Type</span>}
									rules={[{ required: true, message: "Please select a service type." }]}
									style={{ flex: 1, minWidth: 200 }}
								>
									<Select
										placeholder="Select Service Type"
										options={serviceTypes.map((service) => ({ value: service, label: capitalize(service) }))}
										onChange={() => form.setFieldsValue({ folder: undefined, equipmentKeys: [] })}
									/>
								</Form.Item>
							</Flex>
							<Flex gap={12} wrap="wrap">
								<Form.Item
									name="customerType"
									label={<span className="custom-url-field-label">Customer Type</span>}
									initialValue="all"
									style={{ flex: 1, minWidth: 200, marginBottom: 0 }}
								>
									<Select
										options={customerTypeOptions}
										onChange={(value) => {
											if (value === "new") form.setFields([{ name: "audience", value: "non_member", errors: [] }]);
										}}
									/>
								</Form.Item>
								<Form.Item
									name="audience"
									label={<span className="custom-url-field-label">Memberships</span>}
									initialValue="all"
									style={{ flex: 1, minWidth: 200, marginBottom: 0 }}
								>
									<Select options={audienceOptions} disabled={customerType === "new"} />
								</Form.Item>
							</Flex>
						</Panel>

						<Panel header="Step 2: Job & Pricing" key="2">
							<Form.Item
								name="folder"
								label={
									<span className="custom-url-field-label">
										Job
										<FieldTip text="The choice the customer taps, such as No Cooling or Cooling Tune-Up. Equipment is the machine after this." />
									</span>
								}
								rules={[{ required: true, message: "Please select a job." }]}
								className="promo-single-field"
							>
								<Select
									showSearch
									optionFilterProp="label"
									placeholder="Select Job"
									optionRender={(option) => (
										<Tooltip title={option.label} placement="right">
											<span className="promo-option-label">{option.label}</span>
										</Tooltip>
									)}
									options={folders.map((folder) => ({ value: folder.name, label: folder.name }))}
									onChange={(value) => {
										const folder = folders.find((entry) => entry.name === value);
										form.setFieldsValue({
											equipmentKeys: folder?.isJob ? folder.equipment.map((item) => item.key) : [],
										});
									}}
								/>
							</Form.Item>
							<Form.Item
								name="equipmentKeys"
								label={
									<span className="custom-url-field-label">
										Equipment
										<FieldTip text="The machine for that job, such as Heat Pump or Central A/C. Only enabled equipment is listed." />
									</span>
								}
								rules={[{ required: true, message: "Please select at least one piece of equipment." }]}
							>
								<Select
									mode="multiple"
									showSearch
									optionFilterProp="label"
									placeholder="Select Equipment"
									disabled={Boolean(currentFolder?.isJob)}
									options={equipmentOptions.map((item) => ({ value: item.key, label: item.name }))}
									dropdownRender={(menu) => (
										<>
											{!currentFolder?.isJob && equipmentOptions.length > 0 && (
												<div style={{ padding: "4px 8px", borderBottom: "1px solid #f0f0f0" }}>
													<div style={{ display: "flex", gap: "6px" }}>
														<Button
															size="small"
															type="link"
															onMouseDown={(event) => event.preventDefault()}
															onClick={() => form.setFieldValue("equipmentKeys", equipmentOptions.map((item) => item.key))}
															style={{ fontSize: "11px", padding: 0, height: "auto", color: "#1890ff" }}
														>
															✓ Select All
														</Button>
														<Button
															size="small"
															type="link"
															onMouseDown={(event) => event.preventDefault()}
															onClick={() => form.setFieldValue("equipmentKeys", [])}
															style={{ fontSize: "11px", padding: 0, height: "auto", color: "#ff4d4f" }}
														>
															✗ Clear All
														</Button>
													</div>
												</div>
											)}
											{menu}
										</>
									)}
								/>
							</Form.Item>
							<Flex gap={12} align="flex-end" className="promo-price-row">
								<Form.Item
									name="regularPrice"
									label={
										<span className="custom-url-field-label">
											Regular Price
											<FieldTip text="The compare-at price shown crossed out. It can differ from the dispatch fee already set on the job." />
										</span>
									}
									initialValue={0}
									rules={[{ required: true, message: "Required" }]}
								>
									<InputNumber min={0} max={9999.99} precision={2} step={0.01} prefix="$" style={{ width: 140, ...fieldStyle }} />
								</Form.Item>
								<Form.Item
									name="promoPrice"
									label={
										<span className="custom-url-field-label">
											Promo Price
											<FieldTip text="The price the customer pays while this promotion is on. It replaces the usual dispatch fee for that booking." />
										</span>
									}
									initialValue={0}
									rules={[{ required: true, message: "Required" }]}
								>
									<InputNumber min={0} max={9999.99} precision={2} step={0.01} prefix="$" style={{ width: 140, ...fieldStyle }} />
								</Form.Item>
								<span className="custom-url-field-helper promo-job-save">
									Saves ${Math.max((Number(regularPrice) || 0) - (Number(promoPrice) || 0), 0).toFixed(2)}
								</span>
							</Flex>
							<p className="custom-url-field-helper">
								Cross-check the dispatch fee body on this job. This offer does not change that text, so confirm it matches the promotional price.
							</p>
						</Panel>

						<Panel header="Step 3: Schedule" key="3">
							<Form.Item
								name="scheduleMode"
								label={<span className="custom-url-field-label">How long it runs</span>}
								initialValue="scheduled"
								className="promo-single-field"
							>
								<Select
									options={[
										{ value: "until_disabled", label: "On until turned off" },
										{ value: "scheduled", label: "Start and end dates" },
									]}
								/>
							</Form.Item>
							<Flex gap={12} wrap="wrap">
								<Form.Item
									name="startsAt"
									label={<span className="custom-url-field-label">Start Date</span>}
									rules={scheduleMode === "scheduled" ? [{ required: true, message: "Please select a start date." }] : []}
									className={scheduleMode === "scheduled" ? undefined : "promo-single-field"}
									style={{ flex: scheduleMode === "scheduled" ? 1 : undefined, minWidth: scheduleMode === "scheduled" ? 200 : undefined }}
								>
									<DatePicker
										showTime
										format="YYYY-MM-DD HH:mm"
										style={{ width: "100%", ...fieldStyle }}
										onOpenChange={(open) => {
											if (!open || form.getFieldValue("startsAt")) return;
											form.setFields([{ name: "startsAt", value: dayjs().tz(timeZone), errors: [] }]);
										}}
									/>
								</Form.Item>
								{scheduleMode === "scheduled" && (
									<Form.Item
										name="endsAt"
										label={<span className="custom-url-field-label">End Date</span>}
										rules={[{ required: true, message: "Please select an end date." }]}
										style={{ flex: 1, minWidth: 200 }}
									>
										<DatePicker showTime format="YYYY-MM-DD HH:mm" style={{ width: "100%", ...fieldStyle }} />
									</Form.Item>
								)}
							</Flex>
							<p className="custom-url-field-helper" style={{ marginTop: 0, marginBottom: 16 }}>
								Dates and times use the organization time zone ({timeZone}).
							</p>
							<div style={{ marginBottom: 16 }}>
								<span className="custom-url-field-label">Banner Color</span>
								<Flex align="center" gap={12}>
									<Form.Item name="bannerColor" noStyle>
										<BannerColorPicker />
									</Form.Item>
									<Button
										type="link"
										onClick={() => form.setFieldValue("bannerColor", null)}
										style={{ padding: 0, height: "auto" }}
									>
										Reset
									</Button>
								</Flex>
								<p className="custom-url-field-helper">Leave empty to use the organization color.</p>
							</div>
							<Form.Item
								name="status"
								label={<span className="custom-url-field-label">Status</span>}
								initialValue="inactive"
								className="promo-single-field"
								style={{ marginBottom: 0 }}
							>
								<Select
									options={[
										{ value: "active", label: "Active" },
										{ value: "inactive", label: "Inactive" },
									]}
								/>
							</Form.Item>
						</Panel>
					</Collapse>
				</Form>
			</Modal>
			<Modal
				title={viewing?.title || "Scheduler"}
				open={Boolean(viewing)}
				onCancel={() => setViewing(null)}
				footer={null}
				width={420}
			>
				<p className="custom-url-field-helper" style={{ marginTop: 0, marginBottom: 10 }}>
					How customers see this on the Dispatch Fee screen. The fee on that screen uses the promo price.
				</p>
				<SchedulerPreview
					title={viewing?.title}
					color={/^#[0-9a-fA-F]{6}$/.test(viewing?.bannerColor || "") ? viewing.bannerColor : primaryColor}
					regularCents={viewing?.regularPriceCents}
					promoCents={viewing?.promoPriceCents}
					validThrough={viewing?.endsAt ? dayjs(viewing.endsAt).tz(timeZone).format("MMM D, YYYY h:mm A") : ""}
				/>
			</Modal>
		</>
	);
}
